import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useT } from "../helpers/useT";
import {
  constructApiUrl,
  Meta,
  Resource,
  SingleResponse,
  useFetch,
} from "../api/useFetch";
import { transformMarketplaceToJsonApi } from "./marketplaceAdapter";
import { MarketplaceOffersResponse } from "./marketplaceTypes";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from "../components/List/Table";
import { Headline } from "../components/Headline";
import { EmptyListPlaceholder } from "../components/List/EmptyList";
import { Location, Offer } from "./offer";
import { ShiftOffer } from "./shiftOffer";
import { OfferItem } from "./offerItem";
import { DistanceOrDuration } from "./components/DistanceOrDuration";
import CustomDate from "../components/Date";
import { ApiResponseAware } from "../api/ApiResponseAware";
import { tabs } from "./ride";
import Tabs from "../components/Tabs";
import { useMe } from "../me";
import Price from "../components/Price";
import { ServiceClassWithTitle } from "../components/ServiceClass";
import RefreshList from "../list/Refresh";
import PropertyWithTitle from "../components/PropertyWithTitle";
import { OfferDialog } from "./components/OfferDialog";
import { ShiftOfferDialog } from "./components/ShiftOfferDialog";
import { useAcceptOffer } from "./hooks";
import { SkeletonPage, SkeletonTable } from "../components/Skeleton";
import RawFormattedMessage from "../components/RawFormattedMessage";

export type OffersResponse = {
  data: OfferItem[];
  included?: Array<Resource<Location>>;
  meta: Meta;
};
export type OfferResponse = SingleResponse<Offer>;

type SelectedOffer = {
  offer: Resource<Offer>;
  pickupAddress: string | undefined;
  dropoffAddress: string | undefined;
};

export function OfferList() {
  const me = useMe();
  const t = useT();
  const [isMounted, setIsMounted] = useState(false);

  // Cache previous transformed data to prevent unmounting during reload
  const prevTransformedDataRef = useRef<OffersResponse | null>(null);

  // Dialog state at parent level - preserved during OffersTable unmount/remount
  const [selectedOffer, setSelectedOffer] = useState<SelectedOffer | null>(
    null,
  );
  const [selectedShift, setSelectedShift] =
    useState<Resource<ShiftOffer> | null>(null);
  const { acceptOffer, acceptShift, isAccepting } = useAcceptOffer();

  const handleCloseDialog = useCallback(() => setSelectedOffer(null), []);
  const handleCloseShiftDialog = useCallback(() => setSelectedShift(null), []);

  const handleOfferClick = useCallback(
    (
      offer: Resource<Offer>,
      pickupAddress: string | undefined,
      dropoffAddress: string | undefined,
    ) => {
      setSelectedOffer({ offer, pickupAddress, dropoffAddress });
    },
    [],
  );

  const handleShiftClick = useCallback((shift: Resource<ShiftOffer>) => {
    setSelectedShift(shift);
  }, []);

  // Callback to notify OffersTable when an offer is accepted
  const [lastAcceptedOfferId, setLastAcceptedOfferId] = useState<string | null>(
    null,
  );

  const handleAccept = useCallback(
    async (offer: Resource<Offer>) => {
      const result = await acceptOffer(offer);
      setSelectedOffer(null);

      // Notify OffersTable to update goneOfferIds
      if (
        result.success ||
        result.reason === "already_accepted" ||
        result.reason === "offer_canceled"
      ) {
        setLastAcceptedOfferId(offer.id);
      }

      // Trigger immediate refresh when offer is no longer available
      // This happens when:
      // 1. offer_canceled (404): Offer was canceled by admin or guest
      // 2. already_accepted (409/422): Offer was accepted by another chauffeur
      // In both cases, refreshing ensures user sees updated offers immediately
      if (
        result.reason === "offer_canceled" ||
        result.reason === "already_accepted"
      ) {
        // Small delay to avoid race condition where the backend might not
        // have fully processed the change yet
        setTimeout(() => {
          reloadRef.current();
        }, 500);
      }

      return result;
    },
    [acceptOffer],
  );

  const handleAcceptShift = useCallback(
    async (shift: Resource<ShiftOffer>) => {
      const result = await acceptShift(shift);
      setSelectedShift(null);

      if (
        result.success ||
        result.reason === "already_accepted" ||
        result.reason === "offer_canceled"
      ) {
        setLastAcceptedOfferId(shift.id);
      }

      if (
        result.reason === "offer_canceled" ||
        result.reason === "already_accepted"
      ) {
        setTimeout(() => {
          reloadRef.current();
        }, 500);
      }

      return result;
    },
    [acceptShift],
  );

  // Detect client-side mount to prevent SSR hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const onHoldKeyPrefix =
    me.lsp?.state === "on_hold"
      ? "offers.on-hold-lsp"
      : me.state === "on_hold"
        ? "offers.on-hold-chauffeur"
        : null;

  // Don't make API call until user data is loaded (me.hasFetched).
  // useFetch doesn't react to options changes (see useFetch.ts:94), so we
  // must have the headers ready when the URL becomes non-null.
  // Skip the call entirely when the user is on hold — the page will render
  // a static message instead.
  const url =
    !me.hasFetched || onHoldKeyPrefix
      ? null
      : constructApiUrl("/api/v1/chauffeur/offers", {
          sort_by: "start_time",
          order_by: "asc",
        });

  // TEMPORARY WORKAROUND: Manually add headers for marketplace API.
  // The API Gateway authorizer is not properly populating x-user-lsp-id
  // and x-user-bd-id.
  // TODO: Remove once infrastructure authorizer is fixed to populate all
  // required context values.
  const options = me.hasFetched
    ? {
        headers: {
          "x-user-id": me.id,
          "x-user-roles": me.roles.join(","),
          "x-user-lsp-id": me.lsp?.id || "",
          "x-user-bd-id": me.lsp?.businessDistrict?.uuid || "",
        },
      }
    : {};

  const apiCall = useFetch(url, options);

  // Store latest reload function in ref to avoid stale closures in interval
  const reloadRef = useRef(apiCall.reload);

  // Keep ref updated with latest reload function
  useEffect(() => {
    reloadRef.current = apiCall.reload;
  }, [apiCall.reload]);

  // Transform marketplace response to the OfferItem-based shape used by OffersTable
  const transformedData = useMemo(() => {
    // During reload, return previous data to keep component mounted
    if (!apiCall.data || apiCall.isLoading) {
      return prevTransformedDataRef.current;
    }

    const hasMarketplaceStructure =
      typeof apiCall.data === "object" && "items" in apiCall.data;

    if (!hasMarketplaceStructure) {
      console.warn(
        "[OfferList] Data structure mismatch - expected marketplace format",
      );
      return prevTransformedDataRef.current;
    }

    try {
      const transformed = transformMarketplaceToJsonApi(
        apiCall.data as MarketplaceOffersResponse,
      );
      prevTransformedDataRef.current = transformed;
      return transformed;
    } catch (error) {
      console.error("[OfferList] Error transforming marketplace data:", error);
      return prevTransformedDataRef.current;
    }
  }, [apiCall.data, apiCall.isLoading]);

  // Auto-refresh offers list every 15 seconds
  useEffect(() => {
    // Don't start interval until we have:
    // 1. Valid URL (user data loaded)
    // 2. Initial data loaded (prevents refresh during loading state)
    if (!url || !transformedData) {
      return;
    }

    const intervalId = setInterval(() => {
      reloadRef.current(); // Call via ref to avoid stale closure
    }, 15000); // 15 seconds

    // Cleanup: Clear interval on unmount or when dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [url, transformedData]); // Restart interval when URL or data changes

  // Show skeleton loading state during SSR to prevent hydration mismatch.
  if (!isMounted) {
    return <SkeletonPage />;
  }

  if (onHoldKeyPrefix) {
    return (
      <>
        <Headline title={t("rides.title")} flex={true} />
        <Tabs tabs={tabs(me)} />
        <EmptyListPlaceholder
          data={[]}
          title={t(`${onHoldKeyPrefix}.title`)}
          description={t(`${onHoldKeyPrefix}.body`)}
        >
          <></>
        </EmptyListPlaceholder>
      </>
    );
  }

  // Show skeleton table while loading initial data (prevents layout shift)
  // This includes waiting for me data to load (me.hasFetched)
  if ((apiCall.isLoading || !me.hasFetched) && !transformedData) {
    return (
      <>
        <Headline title={t("rides.title")} flex={true}>
          <RefreshList action={() => apiCall.reload()} />
        </Headline>
        <Tabs tabs={tabs(me)} />
        <SkeletonTable />
      </>
    );
  }

  return (
    <>
      <Headline title={t("rides.title")} flex={true}>
        <RefreshList action={() => apiCall.reload()} />
      </Headline>
      <Tabs tabs={tabs(me)} />
      <ApiResponseAware
        apiCall={{ ...apiCall, data: transformedData }}
        render={(response: OffersResponse) => (
          <OffersTable
            response={response}
            onOfferClick={handleOfferClick}
            onShiftClick={handleShiftClick}
            lastAcceptedOfferId={lastAcceptedOfferId}
          />
        )}
      />
      <OfferDialog
        offer={selectedOffer?.offer ?? null}
        pickupAddress={selectedOffer?.pickupAddress}
        dropoffAddress={selectedOffer?.dropoffAddress}
        isOpen={selectedOffer !== null}
        onClose={handleCloseDialog}
        onAccept={handleAccept}
        isAccepting={isAccepting}
      />
      <ShiftOfferDialog
        shift={selectedShift}
        isOpen={selectedShift !== null}
        onClose={handleCloseShiftDialog}
        onAccept={handleAcceptShift}
        isAccepting={isAccepting}
      />
    </>
  );
}

function OffersTable({
  response,
  onOfferClick,
  onShiftClick,
  lastAcceptedOfferId,
}: {
  response: OffersResponse;
  onOfferClick: (
    offer: Resource<Offer>,
    pickupAddress: string | undefined,
    dropoffAddress: string | undefined,
  ) => void;
  onShiftClick: (shift: Resource<ShiftOffer>) => void;
  lastAcceptedOfferId: string | null;
}) {
  const t = useT();
  const [goneOfferIds, setGoneOfferIds] = useState<string[]>([]);

  // Update goneOfferIds when an offer is accepted
  useEffect(() => {
    if (lastAcceptedOfferId) {
      setGoneOfferIds((prevState) => [...prevState, lastAcceptedOfferId]);
    }
  }, [lastAcceptedOfferId]);

  const items = (response?.data ?? []).filter(
    (item) => !goneOfferIds.includes(item.resource.id),
  );

  return (
    <>
      <EmptyListPlaceholder data={items} title={t("offers.empty-list")}>
        <Table>
          <TableHead>
            <TableHeaderRow>
              <TableHeaderCell width={160}>{t("shared.date")}</TableHeaderCell>
              <TableHeaderCell>{t("shared.pickup")}</TableHeaderCell>
              <TableHeaderCell>{t("shared.dropoff")}</TableHeaderCell>
              <TableHeaderCell width={90}>
                {t("shared.service-class")}
              </TableHeaderCell>
              <TableHeaderCell width={80}>{t("shared.price")}</TableHeaderCell>
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            {items.map((item) =>
              item.kind === "shift" ? (
                <ShiftOfferRow
                  key={item.resource.id}
                  shift={item.resource}
                  onShiftClick={onShiftClick}
                >
                  <RawFormattedMessage id="shared.details" />
                </ShiftOfferRow>
              ) : (
                <OfferRow
                  key={item.resource.id}
                  originalOffer={item.resource}
                  included={response.included}
                  onOfferClick={onOfferClick}
                >
                  <RawFormattedMessage id="shared.details" />
                </OfferRow>
              ),
            )}
          </TableBody>
        </Table>
      </EmptyListPlaceholder>
    </>
  );
}

function OfferRow({
  originalOffer,
  included,
  onOfferClick,
  children,
}: {
  originalOffer: Resource<Offer>;
  included: Array<Resource<Location>> | undefined;
  children: React.ReactNode;
  onOfferClick: (
    offer: Resource<Offer>,
    pickupAddress: string | undefined,
    dropoffAddress: string | undefined,
  ) => void;
}) {
  const pickupAddress = included?.find(
    (l) => l.id === originalOffer.relationships?.pickup_location?.data.id,
  )?.attributes.formatted_address_en;

  const dropoffAddress = included?.find(
    (l) => l.id === originalOffer.relationships?.dropoff_location?.data.id,
  )?.attributes.formatted_address_en;

  const handleClick = () => {
    onOfferClick(originalOffer, pickupAddress, dropoffAddress);
  };

  return (
    <TableRow key={originalOffer.id}>
      <TableCell>
        <PropertyWithTitle isList title="shared.date">
          <CustomDate
            value={originalOffer.attributes.starts_at}
            isHighlighted
          />
        </PropertyWithTitle>
      </TableCell>
      <TableCell>
        <PropertyWithTitle isList title="shared.pickup">
          {pickupAddress}
        </PropertyWithTitle>
      </TableCell>
      <TableCell>
        <PropertyWithTitle isList title="shared.dropoff">
          {dropoffAddress}
        </PropertyWithTitle>
      </TableCell>
      <TableCell>
        <ServiceClassWithTitle id={originalOffer.attributes.service_class} />
      </TableCell>
      <TableCell>
        <PropertyWithTitle isList title="shared.price">
          <Price
            currency={originalOffer.attributes.currency}
            value={parseFloat(originalOffer.attributes.price)}
          />
          <DistanceOrDuration ride={originalOffer.attributes} />
          <button
            type="button"
            data-type="tertiary"
            onClick={handleClick}
            aria-label="View offer details"
          >
            {children}
          </button>
        </PropertyWithTitle>
      </TableCell>
    </TableRow>
  );
}

function ShiftOfferRow({
  shift,
  onShiftClick,
  children,
}: {
  shift: Resource<ShiftOffer>;
  onShiftClick: (shift: Resource<ShiftOffer>) => void;
  children: React.ReactNode;
}) {
  const t = useT();
  const attrs = shift.attributes;
  const handleClick = () => onShiftClick(shift);

  return (
    <TableRow key={shift.id}>
      <TableCell>
        <PropertyWithTitle isList title="shared.date">
          <CustomDate value={attrs.starts_at} isHighlighted />
        </PropertyWithTitle>
      </TableCell>
      <TableCell>
        <PropertyWithTitle isList title="offer.shift.operating-area">
          <div>{attrs.operating_area}</div>
          {attrs.subtitle && (
            <div data-testid="shift-subtitle">{attrs.subtitle}</div>
          )}
        </PropertyWithTitle>
      </TableCell>
      <TableCell>
        <PropertyWithTitle isList title="offer.shift.duration">
          <div>
            {t("offer.shift.max-duration", {
              hours: attrs.max_shift_duration_hours,
            })}
          </div>
          {attrs.banner && <div data-testid="shift-banner">{attrs.banner}</div>}
        </PropertyWithTitle>
      </TableCell>
      <TableCell>
        <ServiceClassWithTitle id={attrs.service_class} />
      </TableCell>
      <TableCell>
        <PropertyWithTitle isList title="shared.price">
          <Price currency={attrs.currency} value={parseFloat(attrs.price)} />
          <button
            type="button"
            data-type="tertiary"
            onClick={handleClick}
            aria-label={t("offer.shift.view-details")}
          >
            {children}
          </button>
        </PropertyWithTitle>
      </TableCell>
    </TableRow>
  );
}
